import React, { useState } from "react";
import { useParams } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp"; // Custom hook for API calls
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  IconButton,
  Alert,
  Badge,
} from "@mui/material";
import { Send as SendIcon, Person as PersonIcon } from "@mui/icons-material";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";
import { toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
      light: "#e3f2fd",
      contrastText: "#fff",
    },
    secondary: {
      main: "#f50057",
    },
    background: {
      default: "#f5f7fa",
      paper: "#fff",
    },
    text: {
      primary: "#2d3748",
      secondary: "#718096",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.95rem",
    },
    caption: {
      fontSize: "0.8rem",
    },
  },
  components: {
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "4px 8px",
          "&.Mui-selected": {
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
          },
          "&:hover": {
            backgroundColor: "#f0f4f8",
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: "#1976d2",
          fontSize: "0.9rem",
        },
      },
    },
  },
});

// Styled components
const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 320,
  height: "calc(100vh - 16px)",
  overflowY: "auto",
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "2px 0 8px rgba(0,0,0,0.05)",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const ChatContainer = styled(Box)({
  flex: 1,
  height: "calc(100vh - 16px)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: theme.palette.background.default,
});

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
}));

const ChatMessages = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.grey[400],
    borderRadius: "3px",
  },
}));

const MessageBubble = styled(Paper)(({ theme, isSent }) => ({
  padding: theme.spacing(1.5, 2),
  maxWidth: "70%",
  alignSelf: isSent ? "flex-end" : "flex-start",
  backgroundColor: isSent
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  color: isSent
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  borderRadius: isSent ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
  boxShadow: isSent
    ? "0 2px 6px rgba(25, 118, 210, 0.3)"
    : "0 2px 6px rgba(0, 0, 0, 0.1)",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  boxShadow: "0 -2px 4px rgba(0,0,0,0.05)",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 24,
    backgroundColor: theme.palette.grey[100],
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const CampaignChats = () => {
  const { cusId, campaignName } = useParams();
  const [selectedContact, setSelectedContact] = useState(null);
  const [messageInput, setMessageInput] = useState("");
  const [reloadChat, setReloadChat] = useState(false);
  const [sendingMessage, setSendingMessage] = useState("");

  // Fetch contact list
  const {
    data: contactData,
    isLoading: isContactsLoading,
    isError: isContactsError,
    error: contactsError,
  } = useApiEp(
    `/api/campaign-reply-numbers/${cusId}?campaignName=${campaignName}`
  );

  // Fetch chat messages for selected contact
  const {
    data: chatData,
    isLoading: isChatLoading,
    isError: isChatError,
    error: chatError,
  } = useApiEp(
    selectedContact
      ? `/api/user-chats/${cusId}/${selectedContact.phoneNumber}?reload=${reloadChat}`
      : null
  );

  // Handle contact selection
  const handleContactClick = (contact) => {
    setSelectedContact(contact);
  };

  // Handle sending a message (mock implementation)
  const handleSendMessage = async () => {
    if (messageInput.trim()) {
      try {
        setSendingMessage(true);
        const { data } = await axios.post(
          `${API_ENDPOINT}/api/send-message/${cusId}/${selectedContact.phoneNumber}`,
          {
            message: messageInput,
            profileName: selectedContact.profileName,
          }
        );
        setReloadChat((prev) => !prev);
        toast.success(data.message);
      } catch (error) {
        toastError(error);
      } finally {
        setSendingMessage(false);
        setMessageInput("");
      }
    }
  };

  // Determine if the message is sent by the user
  const isMessageSent = (message) => {
    return message.from !== selectedContact?.phoneNumber;
  };

  // Get message content based on type
  const getMessageContent = (message) => {
    if (message.type === "template") {
      const bodyComponent = message.components?.find(
        (component) => component.type === "BODY"
      );
      return bodyComponent?.text || "No content available";
    } else if (message.type === "text" || message.type === "button") {
      return message.body || "No content available";
    }
    return "Unsupported message type";
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{ display: "flex", height: "100vh", bgcolor: "background.default" }}
      >
        {/* Sidebar: Contact List */}
        <SidebarContainer>
          {isContactsLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          {isContactsError && (
            <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
              Error: {contactsError?.message || "Failed to load contacts"}
            </Alert>
          )}
          {contactData?.replies && (
            <List sx={{ p: 1 }}>
              {contactData.replies.map((contact) => (
                <ListItem
                  key={contact.phoneNumber}
                  button
                  selected={
                    selectedContact?.phoneNumber === contact.phoneNumber
                  }
                  onClick={() => handleContactClick(contact)}
                  sx={{ py: 1.5 }}
                >
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      badgeContent={
                        contact.unreadCount ? (
                          <Box
                            sx={{
                              bgcolor: "secondary.main",
                              color: "white",
                              borderRadius: "50%",
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.75rem",
                            }}
                          >
                            {contact.unreadCount}
                          </Box>
                        ) : null
                      }
                    >
                      <Avatar>{contact.profileName[0].toUpperCase()}</Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight={500}>
                        {contact.profileName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {contact.phoneNumber}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </SidebarContainer>

        {/* Chat Panel */}
        <ChatContainer>
          {selectedContact ? (
            <>
              {/* Chat Header */}
              <ChatHeader>
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  {selectedContact.profileName[0].toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {selectedContact.profileName}
                  </Typography>
                  <Typography variant="caption" color="text Pioneer">
                    {selectedContact.phoneNumber}
                  </Typography>
                </Box>
              </ChatHeader>

              {/* Chat Messages */}
              <ChatMessages>
                {isChatLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress size={32} />
                  </Box>
                )}
                {isChatError && (
                  <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
                    Error: {chatError?.message || "Failed to load messages"}
                  </Alert>
                )}
                {chatData?.messages?.map((message) => (
                  <MessageBubble
                    key={message.id}
                    isSent={isMessageSent(message)}
                  >
                    <Typography variant="body1">
                      {getMessageContent(message)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={
                        isMessageSent(message) ? "inherit" : "text.secondary"
                      }
                      sx={{ display: "block", mt: 0.5, opacity: 0.7 }}
                    >
                      {new Date(message.updatedOn).toLocaleTimeString()}
                    </Typography>
                  </MessageBubble>
                ))}
                {!isChatLoading &&
                  !isChatError &&
                  (!chatData?.messages || chatData.messages.length === 0) && (
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 2,
                        color: "text.secondary",
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                      <Typography variant="body1">
                        No messages yet. Start the conversation!
                      </Typography>
                    </Box>
                  )}
              </ChatMessages>

              {/* Message Input */}
              <InputContainer>
                <StyledTextField
                  fullWidth
                  variant="outlined"
                  placeholder="Type a message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                />

                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim() || sendingMessage}
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": { bgcolor: "primary.dark" },
                    borderRadius: "50%",
                    p: 1.2,
                  }}
                >
                  {sendingMessage ? <CircularProgress /> : <SendIcon />}
                </IconButton>
              </InputContainer>
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
                color: "text.secondary",
              }}
            >
              <PersonIcon sx={{ fontSize: 64, opacity: 0.3 }} />
              <Typography variant="h6" fontWeight={500}>
                Select a contact to start chatting
              </Typography>
              <Typography
                variant="body2"
                sx={{ maxWidth: 400, textAlign: "center" }}
              >
                Choose a contact from the list to view your conversation or
                start a new one.
              </Typography>
            </Box>
          )}
        </ChatContainer>
      </Box>
    </ThemeProvider>
  );
};

export default CampaignChats;
